/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import { isEmpty } from '@ember/utils';
import { type Registry as Services, service } from '@ember/service';
import { subDays } from 'date-fns/subDays';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import { WEEKDAYS } from 'uplisting-frontend/models/base-rate';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { isSaveDisabled } from 'uplisting-frontend/utils/changeset';

export type BulkStayStrategyChangeset = GenericChangeset<
  | ModelRegistry['bulk-mlos']
  | ModelRegistry['bulk-rate']
  | ModelRegistry['bulk-closed-for-arrival']
  | ModelRegistry['bulk-closed-for-departure']
>;

type BulkStayStrategyBooleanChangeset = GenericChangeset<
  | ModelRegistry['bulk-closed-for-arrival']
  | ModelRegistry['bulk-closed-for-departure']
>;

export enum SyncType {
  prices = 'prices',
  mlos = 'mlos',
  closedForArrival = 'closed_for_arrival',
  closedForDeparture = 'closed_for_departure',
}

interface ISyncTypeConfig {
  type: 'numeric' | 'boolean';
  translationKey: string;
  faqLink: string;
  translationKeyOnSave?: string;
  bulkEvent?: AnalyticsEvents;
  spotEvent?: AnalyticsEvents;
}

const SYNC_TYPE_CONFIG: Record<SyncType, ISyncTypeConfig> = {
  [SyncType.prices]: {
    type: 'numeric',
    translationKey: 'calendar_rates',
    faqLink: 'how-to-set-and-change-rates-on-uplisting',
    translationKeyOnSave: 'calendar_rates.notifications.saved',
    bulkEvent: AnalyticsEvents.updateBulkRates,
    spotEvent: AnalyticsEvents.updateSpotRates,
  },
  [SyncType.mlos]: {
    type: 'numeric',
    translationKey: 'calendar_mlos',
    faqLink: 'minimum-length-of-stay',
    bulkEvent: AnalyticsEvents.updatedBulkMlos,
  },
  [SyncType.closedForArrival]: {
    type: 'boolean',
    translationKey: 'calendar_bulk_arrival',
    faqLink: 'closed-for-arrival-departure',
    bulkEvent: AnalyticsEvents.updatedClosedForArrival,
  },
  [SyncType.closedForDeparture]: {
    type: 'boolean',
    translationKey: 'calendar_bulk_departure',
    faqLink: 'closed-for-arrival-departure',
    bulkEvent: AnalyticsEvents.updatedClosedForDeparture,
  },
};

interface IArgs {
  syncType: SyncType;
  isBulk?: boolean;
  changeset: BulkStayStrategyChangeset;
}

interface StayStrategyV1Signature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiStayStrategyV1Component extends Component<StayStrategyV1Signature> {
  @service router!: Services['router'];
  @service analytics!: Services['analytics'];
  @service notifications!: Services['notifications'];

  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  minDate = subDays(new Date(), 1);

  @cached
  get routeAfterSave(): string {
    const currentRouteName = this.router.currentRouteName ?? '';

    if (currentRouteName.startsWith('xcalendar')) {
      return 'xcalendar';
    } else if (currentRouteName.startsWith('monthly')) {
      return 'monthly';
    }

    return 'calendar';
  }

  @cached
  get changeset(): BulkStayStrategyChangeset {
    return this.args.changeset;
  }

  @cached
  get booleanChangeset(): BulkStayStrategyBooleanChangeset {
    return this.changeset as BulkStayStrategyBooleanChangeset;
  }

  @cached
  get config(): ISyncTypeConfig {
    return SYNC_TYPE_CONFIG[this.args.syncType];
  }

  @cached
  get translationKey(): string {
    return this.config.translationKey;
  }

  @cached
  get faqLink(): string {
    return this.config.faqLink;
  }

  @cached
  get translationKeyOnSave(): string | undefined {
    return this.config.translationKeyOnSave;
  }

  @cached
  get analyticsEvent(): AnalyticsEvents | undefined {
    return this.args.isBulk ? this.config.bulkEvent : this.config.spotEvent;
  }

  @cached
  get isNumeric(): boolean {
    return this.config.type === 'numeric';
  }

  @cached
  get isBoolean(): boolean {
    return this.config.type === 'boolean';
  }

  @cached
  get isSubmitDisabled(): boolean {
    const { changeset } = this;

    const hasNoProperties = changeset.properties.length === 0;

    if (this.isBoolean) {
      return hasNoProperties;
    }

    const allRatesEmpty = WEEKDAYS.every((day) => isEmpty(changeset[day]));

    const isInvalid = 'isInvalid' in changeset && changeset.isInvalid;

    return isInvalid || hasNoProperties || allRatesEmpty;
  }

  @cached
  get property(): ModelRegistry['property'] | undefined {
    return this.changeset.properties[0];
  }

  @action
  handleDatesSelect(start: Date, end: Date): void {
    Object.assign(this.changeset, {
      from: start,
      to: end,
    });

    this.changeset.validate();
  }

  @action
  @waitFor
  async handleFormSubmit(): Promise<void> {
    if (this.isSubmitDisabled) {
      return;
    }

    try {
      if (!this.args.isBulk) {
        const value = this.changeset.sunday;

        Object.assign(this.changeset, {
          monday: value,
          tuesday: value,
          wednesday: value,
          thursday: value,
          friday: value,
          saturday: value,
        });
      }

      const saveDisabled = await isSaveDisabled(this.changeset);

      if (saveDisabled) {
        return;
      }

      await this.changeset.save();

      this.notifications.info(this.translationKeyOnSave);

      if (this.analyticsEvent) {
        this.analytics.trackEvent(this.analyticsEvent);
      }

      this.calendarManager.data.reloadRatesFor(this.changeset.properties);

      this.router.transitionTo(this.routeAfterSave);
    } catch {
      this.notifications.error();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::StayStrategy::V1': typeof UiStayStrategyV1Component;
  }
}
